import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import BrutalButton from './BrutalButton';

const FAQS = [
  {
    question: "What makes DisruptNext different from other agencies?",
    answer: "We don't do vanity metrics. Every strategy is tied to revenue. Our E3 methodology — Embedded, Essential, Engineered — is built for enterprise complexity: multiple stakeholders, long sales cycles, and multi-crore targets."
  },
  {
    question: "What industries do you work with?",
    answer: "We specialize in high-growth sectors: SaaS, e-commerce, fintech, manufacturing, real estate, and DTC brands. If you're doing 40Cr+ and ready to scale, we're ready to deploy."
  },
  {
    question: "How quickly can we see results?",
    answer: "PPC campaigns show results within days. SEO takes 3-6 months for significant movement. We set realistic expectations and overdeliver — that's how we maintain 94% client retention."
  },
  {
    question: "What is your minimum engagement?",
    answer: "Our minimum engagement starts at ₹10L/month in ad spend. Enterprise performance marketing requires sufficient budget to gather meaningful data and achieve statistical significance."
  },
  {
    question: "Do you offer guaranteed results?",
    answer: "We guarantee our commitment, strategy, and execution. Specific ROI depends on your market, budget, and competition. We'll be brutally honest about what's achievable — that's why clients trust us."
  }
];

const Contact: React.FC = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    revenue: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/xwkzgblo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          company: formState.company,
          revenue: formState.revenue,
          message: formState.message,
          _subject: `New Inquiry from ${formState.company || formState.name}`,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      // Fallback: mailto link
      const subject = encodeURIComponent(`Enterprise Inquiry from ${formState.company || formState.name}`);
      const body = encodeURIComponent(
        `Name: ${formState.name}\nEmail: ${formState.email}\nCompany: ${formState.company}\nAnnual Revenue: ${formState.revenue}\n\nMessage:\n${formState.message}`
      );
      window.open(`mailto:hello@disruptnext.com?subject=${subject}&body=${body}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <section id="contact" className="py-24 bg-brand-black min-h-screen flex items-center relative border-t-8 border-brand-white">
      <div className="max-w-4xl mx-auto px-4 w-full">
        <div className="mb-16 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-red/10 border border-brand-red/30 text-brand-red font-mono text-xs tracking-[0.2em] uppercase mb-6">
            <span className="w-1.5 h-1.5 bg-brand-red rounded-full animate-pulse" />
            Enterprise Inquiries Only
          </span>
          <h2 className="text-5xl md:text-7xl text-brand-white mb-4">
            LET'S <span className="text-brand-red">TALK</span>
          </h2>
          <p className="text-gray-400 font-mono text-base max-w-xl mx-auto">
            Minimum engagement: ₹10L/month. We work with serious businesses 
            ready for serious growth.
          </p>
        </div>

        {submitted ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20 border-2 border-brand-red/30 bg-brand-red/5"
          >
            <CheckCircle className="w-16 h-16 text-brand-red mx-auto mb-6" />
            <h3 className="text-3xl font-sans text-brand-white mb-4">INQUIRY RECEIVED</h3>
            <p className="font-mono text-gray-400 max-w-md mx-auto">
              Our team will review your inquiry and respond within 24 hours. 
              Welcome to the E3 system.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8 bg-brand-gray/10 p-8 md:p-12 border border-brand-gray">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block font-mono text-xs text-brand-red mb-2 uppercase tracking-widest">Your Name</label>
                <input 
                  type="text" 
                  required
                  className="w-full bg-transparent border-b-2 border-brand-gray text-brand-white text-lg p-3 focus:border-brand-red focus:outline-none transition-colors placeholder-gray-700"
                  placeholder="John Doe"
                  value={formState.name}
                  onChange={(e) => setFormState({...formState, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block font-mono text-xs text-brand-red mb-2 uppercase tracking-widest">Email</label>
                <input 
                  type="email" 
                  required
                  className="w-full bg-transparent border-b-2 border-brand-gray text-brand-white text-lg p-3 focus:border-brand-red focus:outline-none transition-colors placeholder-gray-700"
                  placeholder="john@company.com"
                  value={formState.email}
                  onChange={(e) => setFormState({...formState, email: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block font-mono text-xs text-brand-red mb-2 uppercase tracking-widest">Company</label>
                <input 
                  type="text" 
                  required
                  className="w-full bg-transparent border-b-2 border-brand-gray text-brand-white text-lg p-3 focus:border-brand-red focus:outline-none transition-colors placeholder-gray-700"
                  placeholder="Company Name"
                  value={formState.company}
                  onChange={(e) => setFormState({...formState, company: e.target.value})}
                />
              </div>
              <div>
                <label className="block font-mono text-xs text-brand-red mb-2 uppercase tracking-widest">Annual Revenue</label>
                <select 
                  required
                  className="w-full bg-brand-black border-b-2 border-brand-gray text-brand-white text-lg p-3 focus:border-brand-red focus:outline-none transition-colors appearance-none"
                  value={formState.revenue}
                  onChange={(e) => setFormState({...formState, revenue: e.target.value})}
                >
                  <option value="" disabled>Select range</option>
                  <option value="40-50cr">₹40Cr - 50Cr</option>
                  <option value="50-100cr">₹50Cr - 100Cr</option>
                  <option value="100-200cr">₹100Cr - 200Cr</option>
                  <option value="200cr+">₹200Cr+</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block font-mono text-xs text-brand-red mb-2 uppercase tracking-widest">Tell us about your goals</label>
              <textarea 
                rows={4}
                className="w-full bg-transparent border-b-2 border-brand-gray text-brand-white text-base p-3 focus:border-brand-red focus:outline-none transition-colors placeholder-gray-700 resize-none"
                placeholder="What does your business do? What are your growth targets? Any specific challenges?"
                value={formState.message}
                onChange={(e) => setFormState({...formState, message: e.target.value})}
              />
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
              <BrutalButton type="submit" className="w-full sm:w-auto">
                {submitting ? 'SENDING...' : 'SEND INQUIRY'} 
                {!submitting && <Send className="w-4 h-4 inline ml-2" />}
              </BrutalButton>
              <p className="font-mono text-xs text-gray-600">
                We respond within 24 hours.
              </p>
            </div>
          </form>
        )}

        <div className="mt-24 border-t border-brand-gray pt-16">
          <h3 className="text-4xl md:text-5xl text-brand-white mb-12 text-center">
            COMMON <span className="text-brand-red">QUESTIONS</span>
          </h3>
          <div className="space-y-8 max-w-3xl mx-auto">
            {FAQS.map((faq, index) => (
              <div key={index} className="border-l-4 border-brand-red pl-6 py-2">
                <h4 className="text-lg md:text-xl text-brand-white mb-3 font-bold">
                  {faq.question}
                </h4>
                <p className="text-gray-400 font-mono text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
     </section>
    </>
  );
 };

export default Contact;
